import { PlusIcon } from 'lucide-react'
import { useState } from 'react'

import {
  BreadcrumbTrail,
  Button,
  ChartDateRangeFilter,
  ChartFilters,
  DataTable,
  PageHeader,
  SearchInput,
} from '@/components/ui'
import { useDateFilter } from '@/hooks/useDateFilter'
import type { DateRange } from '@/lib/utils/filterByDateRange'
import { useAccount } from '@/modules/account'
import { NEW_CASE_DIALOG_ID } from '@/modules/tools/bankruptcy/components/NewCaseDialog'
import {
  useFilings,
  useLoadingFilings,
  useScreeningLimits,
} from '@/modules/tools/bankruptcy/hooks'
import { useDialogActions } from '@/stores/dialog'
import { screenerColumns } from './columns'
import { ScreeningsBudget } from './ScreeningsBudget'

export const ScreenerDashboard = () => {
  const filings = useFilings()
  const loading = useLoadingFilings()
  const { openDialog } = useDialogActions()
  const screeningLimits = useScreeningLimits()
  const account = useAccount()
  const [dateRange, setDateRange] = useState<DateRange>('all')
  const [search, setSearch] = useState('')

  const dated = useDateFilter(filings, dateRange, { dateField: 'filedOn' })

  return (
    <div className="space-y-6">
      <BreadcrumbTrail page="Bankruptcy Screener" />

      <PageHeader
        title="Bankruptcy Screener"
        subtitle="Every case on your desk, ranked by the money reaching creditors."
      >
        <div className="flex flex-col items-end gap-1.5">
          <Button onClick={() => openDialog(NEW_CASE_DIALOG_ID)}>
            <PlusIcon className="h-4 w-4" />
            New case
          </Button>
          {!account?.isDemo && screeningLimits && (
            <ScreeningsBudget {...screeningLimits} />
          )}
        </div>
      </PageHeader>

      <div className="flex items-center justify-between gap-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by case name or number…"
          className="max-w-sm"
        />
        <ChartFilters>
          <ChartDateRangeFilter
            value={dateRange}
            onValueChange={setDateRange}
            presets={[
              { label: 'Last 30 days', value: '30d' },
              { label: 'Last 90 days', value: '90d' },
              { label: 'This year', value: '1y' },
              { label: 'All time', value: 'all' },
            ]}
          />
        </ChartFilters>
      </div>

      <div className="overflow-hidden rounded-lg border">
        <DataTable
          columns={screenerColumns as any}
          data={dated}
          globalFilter={search}
          height="h-auto"
          initialPageSize={25}
          isLoading={loading}
          emptyMessage="No cases yet. Create one to get started."
        />
      </div>
    </div>
  )
}
